import React from "react";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import StatusTag from "./atoms/StatusTag";
import { TYPE_LABELS, STATUS_LABELS, appLabel, fieldLabel, formatValue, relativeTime } from "../utils/format";

function RequestCard({ item, perspective }) {
  // perspective: "inbox" | "outbox"
  const isAppraisal = item.type === "appraisal_review";
  const isConsult = item.type === "consultation_registration";
  const cs = item.consultation_summary || null;
  const target = isAppraisal
    ? `${item.target?.appraisal_code || "감정"}${
        item.target?.requester_name ? ` · ${item.target.requester_name}` : ""
      }`.trim()
    : isConsult
    ? `${cs?.user_name || "고객"}${cs?.branch_name ? ` · ${cs.branch_name}` : ""}`
    : item.type === "product_price" ||
      item.type === "product_account" ||
      item.type === "product_bump" ||
      item.type === "consignment_return" ||
      item.type === "repair_complete"
    ? `${item.target?.brand_eng || ""} ${item.target?.product_name || ""}${
        item.target?.sku_code ? ` · ${item.target.sku_code}` : ""
      }`.trim()
    : `${item.target?.name || "?"}${
        item.target?.position ? ` (${item.target.position})` : ""
      }`;

  const fields = Object.keys(item.after_data || {});
  const branchesResolved = item.branches_resolved || null;

  return (
    <Card to={`/requests/${item.id}`}>
      <Top>
        <TypeBadge>
          {TYPE_LABELS[item.type] || item.type}
          {item.app && <AppBadge>{appLabel(item.app)}</AppBadge>}
        </TypeBadge>
        <StatusTag status={item.status}>
          {STATUS_LABELS[item.status] || item.status}
        </StatusTag>
      </Top>

      <Title>{target}</Title>

      {isAppraisal ? (
        <SubInfo>
          {item.appraisal_stage_label || "감정 검수"}
          {item.target?.assignee_branch ? ` · ${item.target.assignee_branch}` : ""}
        </SubInfo>
      ) : isConsult ? (
        <SubInfo>
          {cs?.customer_type_label ? `${cs.customer_type_label} · ` : ""}
          상품 {cs?.product_count || 0}건
          {cs?.consultation_date ? ` · ${cs.consultation_date}` : ""}
        </SubInfo>
      ) : item.type === "consignment_return" ? (
        // 위탁 반환 — 필드 diff 없는 상태전이. 요약 문구로 표시
        <SubInfo>
          위탁 반환 요청 · 승인 시 반환완료 전환
          {item.target?.user_name ? ` · 위탁자 ${item.target.user_name}` : ""}
        </SubInfo>
      ) : item.type === "product_bump" && fields.length === 0 ? (
        // level 4+ 가격 무변경 끌어올리기 — 변경 필드가 없으면 요약 문구로 표시
        <SubInfo>가격 변경 없음 · 끌어올리기만</SubInfo>
      ) : (
        <Diffs>
          {fields.slice(0, 3).map((f) => (
            <DiffLine key={f}>
              <DiffLabel>{fieldLabel(f)}</DiffLabel>
              <Old>{formatValue(f, item.before_data?.[f], branchesResolved)}</Old>
              <Arrow>→</Arrow>
              <New>{formatValue(f, item.after_data?.[f], branchesResolved)}</New>
            </DiffLine>
          ))}
        </Diffs>
      )}

      <Bottom>
        <People>
          {perspective === "inbox" ? (
            <>
              <Soft>요청</Soft>
              <Strong>{item.requester?.name || "?"}</Strong>
            </>
          ) : (
            <>
              <Soft>승인자</Soft>
              <Strong>{item.reviewer?.name || "?"}</Strong>
            </>
          )}
        </People>
        <Time>{relativeTime(item.created_at)}</Time>
      </Bottom>
    </Card>
  );
}

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e6e8eb;
  border-radius: 14px;
  text-decoration: none;
  color: #222;
`;
const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #003580;
  font-weight: 500;
`;
const AppBadge = styled.span`
  display: inline-block;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 500;
  color: #555;
  background: #eef0f3;
  border-radius: 8px;
`;
const Title = styled.div`
  font-size: 15px;
  font-weight: 500;
  word-break: break-word;
`;
const Diffs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const SubInfo = styled.div`
  font-size: 13px;
  color: #003580;
  font-weight: 500;
`;
const DiffLine = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr auto 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 13px;
`;
const DiffLabel = styled.span`
  color: #888;
`;
const Old = styled.span`
  color: #888;
  text-decoration: line-through;
  text-align: right;
`;
const Arrow = styled.span`
  color: #888;
`;
const New = styled.span`
  color: #003580;
  font-weight: 500;
`;
const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #888;
`;
const People = styled.div`
  display: flex;
  gap: 6px;
`;
const Soft = styled.span`
  color: #888;
`;
const Strong = styled.span`
  color: #222;
  font-weight: 500;
`;
const Time = styled.span`
  color: #888;
`;

export default RequestCard;
